import React, { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import ChannelDeliverySettings from "./ChannelDeliverySettings";
import ChannelSettings from "./ChannelSettings";
import MediaTypeSettings from "./MediaTypeSettings";
import SchedulingSettings from "./SchedulingSettings";
import FastAPI from "../../../api/FastAPI";
import useFAST from "../../../contexts/FASTContext";
import useFASTLayout from "../../../hooks/useFASTLayout";
import toBase64 from "../../../utils/images";
import SaveButton from "../../form/SaveButton";

import type { JSX } from "react";
import type { SubmitHandler } from "react-hook-form";

import type { MediaFormat } from "../../../constants/media";
import type { AppState } from "../../../reducers";
import type {
  ChannelSettingsFieldValues,
  KeyWithTranslation,
} from "../../../types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface Props {
  mediaTypeList: {
    label: string;
    key: MediaFormat;
    format?: KeyWithTranslation;
  }[];
}

const DEFAULT_FIRST_DAY_OF_WEEK = "monday";
const DEFAULT_FIRST_HOUR_OF_DAY = "00";

export default function SettingsForm({ mediaTypeList }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("common");
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const { state: fastState } = useFAST();
  const languagesById = useSelector(
    (state: AppState) => state.languages.bySlugAndId,
  );
  const formId = "channel-settings-form";

  useFASTLayout({
    title: t("channel-settings", { ns: "editor-fast-channel" }),
    button: <SaveButton formId={formId} />,
  });

  const channelMediaColors = mediaTypeList?.reduce((acc, currMediaColor) => {
    const currentKey = currMediaColor.key;
    const existingColor = fastState.channelData?.medias_colors?.[currentKey];
    const defaultColor = "#FFFFFF";

    if (existingColor) {
      return {
        ...acc,
        [currentKey]: existingColor,
      };
    }

    return {
      ...acc,
      [currentKey]: defaultColor,
    };
  }, {});

  const languageCode =
    fastState.channelData?.language_uuid &&
    languagesById[fastState.channelData?.language_uuid]?.language;

  const translatedLanguage = t(`langs.${languageCode}`, { ns: "languages" });

  const methods = useForm<ChannelSettingsFieldValues>({
    values: {
      channelDeliveryName: fastState.channelData?.channel_delivery_name ?? "",
      channelDescription: fastState.channelData?.channel_description ?? "",
      channelShortDescription:
        fastState.channelData?.channel_short_description ?? "",
      channelRating: {
        value: fastState.channelData?.channel_main_rating ?? "",
      },
      channelDeliveryRegions:
        fastState.channelData?.channel_delivery_regions?.map((region) => ({
          value: region,
        })) ?? [],
      channelGenres: fastState.channelData?.channel_genres ?? [],
      channelName: fastState.channelData?.name ?? "",
      productionLanguage: languageCode ? translatedLanguage : "",
      timezone: fastState.channelData?.timezone ?? "",
      firstDayOfWeek: {
        value:
          fastState.channelData?.first_day_of_week ?? DEFAULT_FIRST_DAY_OF_WEEK,
      },
      firstHourOfDay: {
        value:
          fastState.channelData?.daily_start_time?.split(":")[0] ??
          DEFAULT_FIRST_HOUR_OF_DAY,
      },
      mediaColors: channelMediaColors,
      channelLogoUrl: fastState.channelData?.logo_url,
      channelLogoWidth: fastState.channelData?.logo_width,
      channelLogoHeight: fastState.channelData?.logo_height,
      channelLogoPosition: { value: fastState.channelData?.logo_position },
    },
  });

  const {
    mutate: uploadLogo,
    isLoading: isUploadingLogo,
    isSuccess: isUploadSuccess,
    isError: isUploadError,
  } = useMutation({
    mutationFn: async (base64Logo: string) =>
      FastAPI.uploadChannelLogo({
        channelId,
        base64Logo,
        onUploadProgress: (progressEvent) => {
          const progressPercentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadPercentage(progressPercentage);
        },
      }),
    onError: () => {
      toast.error(t("channelLogoUploadError", { ns: "editor-fast-channel" }));
      methods.resetField("channelLogoFile");
    },
    onSettled: () => {
      queryClient.invalidateQueries(["getChannel", channelId]);
    },
  });

  const { mutate: updateChannel } = useMutation({
    mutationFn: async ({
      firstDayOfWeek,
      firstHourOfDay,
      channelName,
      mediaColors,
      channelLogoWidth,
      channelLogoHeight,
      channelLogoPosition,
      channelDescription,
      channelShortDescription,
      channelRating,
      channelDeliveryRegions,
      channelGenres,
    }: Pick<
      ChannelSettingsFieldValues,
      | "firstDayOfWeek"
      | "firstHourOfDay"
      | "channelName"
      | "mediaColors"
      | "channelLogoWidth"
      | "channelLogoHeight"
      | "channelLogoPosition"
      | "channelDescription"
      | "channelShortDescription"
      | "channelRating"
      | "channelDeliveryRegions"
      | "channelGenres"
    >) =>
      FastAPI.updateChannel({
        channelId,
        data: {
          first_day_of_week:
            typeof firstDayOfWeek === "string"
              ? firstDayOfWeek
              : firstDayOfWeek.value,
          daily_start_time: `${firstHourOfDay.value}:00:00`,
          name: channelName,
          medias_colors: mediaColors,
          logo_width: Number(channelLogoWidth),
          logo_height: Number(channelLogoHeight),
          logo_position:
            typeof channelLogoPosition === "string"
              ? channelLogoPosition
              : channelLogoPosition?.value,
          channel_description: channelDescription,
          channel_short_description: channelShortDescription,
          channel_main_rating:
            typeof channelRating === "string"
              ? channelRating
              : channelRating.value,
          channel_delivery_regions: channelDeliveryRegions?.map(
            (region) => region.value,
          ),
          channel_genres: channelGenres,
        },
      }),
    onSuccess: () => {
      toast.success(t("saveSuccess", { ns: "common" }));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
    onSettled: () => {
      if (!isUploadingLogo) {
        queryClient.invalidateQueries(["getChannel", channelId]);
      }
    },
  });

  const handleFormSubmit: SubmitHandler<ChannelSettingsFieldValues> = async (
    values,
  ): Promise<void> => {
    const {
      firstDayOfWeek,
      firstHourOfDay,
      channelName,
      mediaColors,
      channelLogoFile,
      channelLogoWidth,
      channelLogoHeight,
      channelLogoPosition,
      channelDescription,
      channelShortDescription,
      channelRating,
      channelDeliveryRegions,
      channelGenres,
    } = values;

    if (channelLogoFile) {
      try {
        const base64Logo = await toBase64(channelLogoFile);

        if (typeof base64Logo !== "string") {
          throw new Error("Error converting logo to base64");
        }

        uploadLogo(base64Logo);
      } catch (error) {
        console.error(error);
        toast.error(t("channelLogoUploadError", { ns: "editor-fast-channel" }));
      }
    }

    updateChannel({
      firstDayOfWeek,
      firstHourOfDay,
      channelName,
      mediaColors,
      channelLogoWidth,
      channelLogoHeight,
      channelLogoPosition,
      channelDescription,
      channelShortDescription,
      channelRating,
      channelDeliveryRegions,
      channelGenres,
    });
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <form
        className="relative flex flex-col gap-[0.78125rem] pb-[0.78125rem]"
        id={formId}
        onSubmit={methods.handleSubmit((data) => handleFormSubmit(data))}
      >
        <ChannelSettings
          isUploadError={isUploadError}
          isUploadSuccess={isUploadSuccess}
          uploadPercentage={uploadPercentage}
        />
        <SchedulingSettings />
        <ChannelDeliverySettings />
        <MediaTypeSettings mediaTypeList={mediaTypeList} />
      </form>
    </FormProvider>
  );
}
